/**
 * pageLevels — the one list of which signed-in pages are top-level (reached from
 * the top bar, no Back button) and which are child pages (always a Back button
 * whose fallback is the logical parent below).
 *
 * The header, BackButton fallbacks and `src/test/pageLevels.test.ts` all read
 * this list, so a new page cannot quietly ship without a way back.
 */

/**
 * Use as a BackButton fallback for pages any persona reaches (settings, support):
 * resolves to the signed-in person's landing page (Buy Requests for buyers,
 * Your Desk for sellers), or the marketing home when signed out.
 */
export const LANDING_FALLBACK = '@landing';

/** Top-bar destinations. Never render a Back button on these. */
export const TOP_LEVEL_PATHS = [
  '/desk',
  '/my-listings',
  '/my-menu',
  '/buy-requests',
  '/supplier-menus',
  '/messages',
] as const;

/** Child pages → their logical parent (the BackButton fallback). */
export const CHILD_PAGE_PARENTS: Record<string, string> = {
  '/watchlist': '/buy-requests',
  '/listings/:id': '/my-listings',
  '/listings/:id/edit': '/my-listings',
  '/menu/:companyId': '/supplier-menus',
  '/menu/:companyId/:menuId': '/supplier-menus',
  '/menu/:companyId/:menuId/listing/:listingId': '/supplier-menus',
  '/company/:companyId': '/supplier-menus',
  '/my-menu/:menuId': '/my-menu',
  '/menus/:menuId': '/my-menu',
  '/company-settings': LANDING_FALLBACK,
  '/profile-settings': LANDING_FALLBACK,
  '/pricing': '/company-settings',
  '/settings/email': '/profile-settings',
  '/support': LANDING_FALLBACK,
};

export function isTopLevelPath(pathname: string): boolean {
  return (TOP_LEVEL_PATHS as readonly string[]).includes(pathname);
}

export function parentOf(routePattern: string, fallback = '/'): string {
  return CHILD_PAGE_PARENTS[routePattern] ?? fallback;
}
